import React, { useState, useEffect } from 'react';
import './PlanConfig.css';
import Navbar from '../../components/Navbar/Navbar';
import { getPlanConfig, calculateUpgrade, createOrder, verifyPayment } from '../../services/subscriptionApis';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPlanSelection, clearPlanSelection, setCookie } from '../../utils/cookies';
import usePlanExpiredPopupStore from '../../store/Useplanexpiredpopupstore';
import { SuccessAlert } from '../../components/common/ErrorAlert/SuccessAlert';



const PlanConfigure = () => {
    const navigate = useNavigate();

    const [billing, setBilling] = useState('yearly');
    const [qty, setQty] = useState({ users: 0, maps: 0, ai: 0 });
    const [planDetails, setPlanDetails] = useState(null);
    const [addonPrices, setAddonPrices] = useState(null);
    const [defaultQuotas, setDefaultQuotas] = useState({ users: 1, maps: 0, ai: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [upgradeData, setUpgradeData] = useState(null);  // proration data
    const [isUpgradeMode, setIsUpgradeMode] = useState(false);
    const [oldSubId, setOldSubId] = useState(null);
    const [carryOverQuota, setCarryOverQuota] = useState({ maps: 0, ai: 0 });

    const [scheduledStartDate, setScheduledStartDate] = useState(null);
    const location = useLocation();

    const stateData = location.state;
    const { planId: cookiePlanId, billingCycle: cookieCycle } = getPlanSelection();
    const planId = stateData?.planId || cookiePlanId;

    // ── Load BOTH SDKs once ────────────────────────────────────────────────
    useEffect(() => {
        // Razorpay
        if (!document.getElementById('razorpay-sdk')) {
            const s = document.createElement('script');
            s.id = 'razorpay-sdk';
            s.src = 'https://checkout.razorpay.com/v1/checkout.js';
            s.async = true;
            document.body.appendChild(s);
        }
        // Stripe
        if (!document.getElementById('stripe-sdk')) {
            const s = document.createElement('script');
            s.id = 'stripe-sdk';
            s.src = 'https://js.stripe.com/v3/';
            s.async = true;
            document.body.appendChild(s);
        }
    }, []);

    // ── On mount: read cookies → set billing → fetch plan ─────────────────

    useEffect(() => {
        if (!planId) { navigate('/home'); return; }

        const cookieBilling = stateData?.billingCycle || cookieCycle;
        const cycle = (cookieBilling === 'monthly' || cookieBilling === 'yearly')
            ? cookieBilling : 'yearly';
        setBilling(cycle);

        const currentPlanData = stateData?.currentPlan || null;  // ← ADD THIS LINE BACK
        const isRenewal = stateData?.is_renewal ?? false;

        const fetchPlan = async () => {
            if (!planId) { navigate('/home'); return; }
            try {
                // const token = localStorage.getItem('accessToken');
                const plan = await getPlanConfig(planId);
                if (plan.is_freeplan) { navigate('/home'); return; }

                setPlanDetails(plan);

                setAddonPrices({
                    yearly: { users: parseFloat(plan.addon_prices.yearly.users), maps: parseFloat(plan.addon_prices.yearly.maps), ai: parseFloat(plan.addon_prices.yearly.ai) },
                    monthly: { users: parseFloat(plan.addon_prices.monthly.users), maps: parseFloat(plan.addon_prices.monthly.maps), ai: parseFloat(plan.addon_prices.monthly.ai) },
                });

                const defMaps = cycle === 'yearly'
                    ? (plan.default_maps_calls_yearly ?? 0)
                    : (plan.default_maps_calls_monthly ?? 0);

                const defAI = cycle === 'yearly'
                    ? (plan.default_ai_credits_yearly ?? 0)
                    : (plan.default_ai_credits_monthly ?? 0);

                setDefaultQuotas({ users: plan.default_users, maps: defMaps, ai: defAI });

                const isUpgrade = stateData?.is_upgrade ?? false;
                setIsUpgradeMode(isUpgrade);

                if (isUpgrade) {
                    try {
                        const ud = await calculateUpgrade(planId, cycle);
                        console.log('Upgrade data fetched:', ud);
                        setUpgradeData(ud);
                        setOldSubId(ud.current_plan?.current_sub_id || null);

                        // Pre-fill with carry-over counts - priority for upgrades
                        const coMaps = ud.new_plan?.carry_over_maps ?? 0;
                        const coAI = ud.new_plan?.carry_over_ai ?? 0;
                        setCarryOverQuota({ maps: coMaps, ai: coAI });

                        setQty({
                            users: ud.new_plan?.carry_over_users ?? 0,
                            maps: 0, // Carry-over maps are now included in default, so stepper starts at 0
                            ai: 0,   // Carry-over AI credits are now included in default, so stepper starts at 0
                        });

                        // Update default quotas immediately to include carry-overs
                        setDefaultQuotas({
                            users: plan.default_users,
                            maps: defMaps + coMaps,
                            ai: defAI + coAI
                        });
                    } catch (err) {
                        console.error('Failed to fetch upgrade data:', err);
                    }
                } else if (currentPlanData) {
                    // pre-fill from existing subscription limits to maintain quotas for non-upgrade cases (renewals)
                    const extraUsers = Math.max(0, currentPlanData.user_limit - plan.default_users);
                    const extraMapUnits = Math.max(0, Math.round((currentPlanData.maps_calls_limit - defMaps) / 1000));
                    const extraAIUnits = Math.max(0, Math.round((currentPlanData.ai_credits_limit - defAI) / 1000));
                    setQty({ users: extraUsers, maps: extraMapUnits, ai: extraAIUnits });

                    if (!isRenewal && !currentPlanData.is_expired) {
                        setScheduledStartDate(currentPlanData.end_date);
                    }
                } else {
                    // fresh — start from zero
                    setQty({ users: 0, maps: 0, ai: 0 });
                }

            } catch (err) {
                console.error('Failed to fetch plan details:', err);
                setError('Failed to load plan. Please refresh and try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlan();
    }, [planId]);

    // ── Recalculate default quotas + keep same TOTAL when billing toggles ──────
    useEffect(() => {
        if (!planDetails) return;

        const newDefaultMaps = billing === 'yearly'
            ? planDetails.default_maps_calls_yearly
            : planDetails.default_maps_calls_monthly;

        const newDefaultAI = billing === 'yearly'
            ? planDetails.default_ai_credits_yearly
            : planDetails.default_ai_credits_monthly;

        setDefaultQuotas({
            users: planDetails.default_users,
            maps: newDefaultMaps + carryOverQuota.maps,
            ai: newDefaultAI + carryOverQuota.ai,
        });

        // ✅ Don't recalculate qty from defaultQuotas (causes drift on toggle)
        // Just preserve whatever extra units the user has set — don't touch them
        // The carry-overs live in defaultQuotas, qty only tracks user-added extras
        setQty(prev => ({
            users: prev.users,
            maps: prev.maps,  // ✅ keep as-is
            ai: prev.ai,      // ✅ keep as-is
        }));

    }, [billing, planDetails, carryOverQuota]);

    // ── Derived pricing ────────────────────────────────────────────────────
    const p = addonPrices?.[billing] ?? { users: 0, maps: 0, ai: 0 };
    const basePrice = planDetails
        ? parseFloat(billing === 'yearly' ? planDetails.yearly_price : planDetails.monthly_price)
        : 0;

    const planLabel = billing === 'yearly' ? 'Annual Billing' : 'Monthly Billing';
    const planName = planDetails?.display_name || planDetails?.name || '—';
    const currencySymbol = planDetails?.plan_currency || '₹';
    const currencyCode = planDetails?.plan_currency_code || 'INR';

    const extraUsers = qty.users;
    const userCost = extraUsers * p.users;
    const mapsCost = qty.maps * p.maps;
    const aiCost = qty.ai * p.ai;
    const total = basePrice + userCost + mapsCost + aiCost;

    const totalUsers = (planDetails?.default_users || 0) + qty.users;
    const totalMaps = defaultQuotas.maps + qty.maps * 1000;
    const totalAI = defaultQuotas.ai + qty.ai * 1000;

    function fmt(n) {
        return currencySymbol + Math.round(n).toLocaleString('en-IN');
    }

    // ── Handlers ──────────────────────────────────────────────────────────
    const change = (key, delta) => {
        setQty(prev => ({
            ...prev,
            [key]: Math.max(0, prev[key] + delta),
        }));
    };

    const handleBillingChange = (cycle) => {
        setBilling(cycle);
        setCookie('selected_billing_cycle', cycle, 7);
    };

    // ── Razorpay checkout ──────────────────────────────────────────────────
    const openRazorpay = (orderData, token) => {
        if (!window.Razorpay) {
            setError('Razorpay SDK not loaded. Please refresh the page.');
            setIsSubmitting(false);
            return;
        }

        const options = {
            key: orderData.key,                      // from server
            amount: orderData.amount,                   // paise
            currency: orderData.currency || 'INR',
            name: 'SalesRoute',
            description: `${orderData.plan_name || planName} – ${orderData.billing_label || planLabel}`,
            order_id: orderData.razorpay_order_id,

            handler: async (response) => {
                try {
                    const result = await verifyPayment({
                        ...response,
                        subscription_id: orderData.subscription_id,
                    });

                    clearPlanSelection();
                    usePlanExpiredPopupStore.getState().clearPlanExpiredPopup();

                    // ✅ Show contextual alert based on subscription type
                    const type = result?.subscription_type;
                    if (type === 'new') {
                        SuccessAlert('🎉 Welcome! Your subscription is now active.');
                    } else if (type === 'renewal') {
                        SuccessAlert('✅ Plan renewed successfully! Your access continues.');
                    } else if (type === 'upgrade') {
                        SuccessAlert('🚀 Plan upgraded successfully!');
                    } else if (type === 'addon') {
                        SuccessAlert('✅ Plan customized successfully!');
                    } else {
                        SuccessAlert('Payment successful!');
                    }

                    navigate('/dashboard');
                    // SuccessAlert("Plan upgraded successfully")

                } catch (err) {
                    console.error('Payment verification failed:', err.response?.data || err);
                    setError(
                        err.response?.data?.message ||
                        'Payment verification failed. Please contact support.'
                    );
                } finally {
                    setIsSubmitting(false);
                }
            },

            modal: {
                // User closed the Razorpay modal without paying
                ondismiss: () => {
                    setIsSubmitting(false);
                    setError('Payment was cancelled. You can try again.');
                },
            },

            prefill: {
                // Optional — fill known user details
                email: localStorage.getItem('userEmail') || '',
            },

            theme: { color: '#0080D7' },
        };

        const rzp = new window.Razorpay(options);

        rzp.on('payment.failed', (response) => {
            console.error('Razorpay payment failed:', response.error);
            setError(
                response.error?.description ||
                'Payment failed. Please try again.'
            );
            setIsSubmitting(false);
        });

        rzp.open();
    };

    // ── Stripe handler — NEW ──────────────────────────────────────────────
    const openStripe = async (orderData) => {
        if (!window.Stripe) {
            setError('Stripe SDK not loaded. Please refresh.');
            setIsSubmitting(false);
            return;
        }
        const stripe = window.Stripe(orderData.stripe_pub_key);
        const { error } = await stripe.redirectToCheckout({
            sessionId: orderData.stripe_session_id,
        });
        if (error) {
            setError(error.message);
            setIsSubmitting(false);
        }
        // on success Stripe redirects to /payment/stripe/success automatically
    };

    // ── Continue button ────────────────────────────────────────────────────
    const handleContinue = async () => {
        setError('');
        setIsSubmitting(true);

        const stateData = location.state;
        const { planId: cookiePlanId } = getPlanSelection();
        const planId = stateData?.planId || cookiePlanId;

        const token = localStorage.getItem('accessToken');

        if (!token) {
            setError('Session expired. Please login again.');
            setIsSubmitting(false);
            return;
        }

        if (!planId) {
            setError('Plan selection lost. Please go back and select a plan.');
            setIsSubmitting(false);
            return;
        }

        const payload = {
            plan_id:       planId,
            billing_cycle: billing,
            extra_users:   extraUsers,
            extra_maps:    qty.maps,
            extra_ai:      qty.ai,
            carry_over_maps: Math.floor(carryOverQuota.maps / 1000),
            carry_over_ai:   Math.floor(carryOverQuota.ai   / 1000),
            currency:      currencyCode,   // ← was planDetails?.currency_code || 'INR'
            activate_after: (!location.state?.currentPlan?.is_expired && !isUpgradeMode && scheduledStartDate)
                ? scheduledStartDate : '',
            is_upgrade:       isUpgradeMode,
            old_sub_id:       oldSubId || '',
            proration_credit: upgradeData?.new_plan?.proration_credit
                || upgradeData?.proration?.total_credit
                || 0,
        };

        try {
            const orderData = await createOrder(payload);

            if (orderData.gateway === 'stripe') {
                await openStripe(orderData);   // redirects away — no return needed
                return;
            }

            if (orderData.razorpay_order_id) {
                openRazorpay(orderData);       // opens modal — isSubmitting stays true
                return;
            }

            // Fallback: navigate to a custom payment page
            navigate('/payment', { state: { orderData, config: payload } });

        } catch (err) {
            console.error('Order creation failed:', err.response?.data || err);
            setError(
                err.response?.data?.message ||
                err.response?.data?.detail ||
                'Failed to create order. Please try again.'
            );
            setIsSubmitting(false);
        }
    };

    const capitalizeFirst = (str) => {
        if (!str) return '';
        str = str.trim();
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // ── Loading state ──────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="app">
                <Navbar />
                <div className="checkout-stepper-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <p className="common-text fts-16 ftw-4" style={{ color: '#475569' }}>Loading plan details…</p>
                </div>
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className='app'>
            <Navbar />
            <div className="checkout-stepper-wrapper">

                {/* ── Stepper ── */}
                <div className="checkout-stepper">
                    <div className="checkout-step done">
                        <div className="checkout-step-bubble active">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3333 4L5.99999 11.3333L2.66666 8" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        Plan
                    </div>
                    <div className="checkout-step-connector active" />
                    <div className="checkout-step active">
                        <div className="checkout-step-bubble active">2</div>
                        Configure
                    </div>
                    <div className="checkout-step-connector" />
                    <div className="checkout-step">
                        <div className="checkout-step-bubble">3</div>
                        Payment
                    </div>
                </div>

                <div className="sub-configure-container">

                    {/* ── Header ── */}
                    <div className="sub-configure-header">
                        <div className="sub-configure-header-left">
                            <h1>Configure Your Plan</h1>
                            <p className="sub-configure-subtitle">Customize your setup based on your business needs</p>
                        </div>
                        {!isUpgradeMode && (
                            <button
                                className="cost-change-plan-btn"
                                onClick={() => navigate('/plans')}
                            >
                                Change Plan
                            </button>
                        )}
                        <div className="sub-configure-header-right">
                            <div className="billing-cycle-toggle-wrapper">
                                <div className="billing-cycle-toggle">
                                    <div className="billing-cycle-option-wrap">
                                        <input
                                            type="radio" name="billing" id="billing-yearly"
                                            checked={billing === 'yearly'}
                                            onChange={() => handleBillingChange('yearly')}
                                        />
                                        <label htmlFor="billing-yearly" className="billing-cycle-option-label">
                                            Yearly <span className="billing-cycle-savings-badge">(Save 30%)</span>
                                        </label>
                                    </div>
                                    <div className="billing-cycle-option-wrap">
                                        <input
                                            type="radio" name="billing" id="billing-monthly"
                                            checked={billing === 'monthly'}
                                            onChange={() => handleBillingChange('monthly')}
                                        />
                                        <label htmlFor="billing-monthly" className="billing-cycle-option-label">
                                            Monthly
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="sub-configure-body">

                        {/* ── Left Column ── */}
                        <div className="sub-configure-left-col">

                            {/* Selected Plan Card */}
                            <div className="selected-plan-card top">
                                <div className="selected-plan-card-header">
                                    <div>
                                        <div className="selected-plan-label">Selected Plan</div>
                                        <div className="selected-plan-name">{capitalizeFirst(planName)}</div>
                                    </div>
                                    <div className="selected-plan-pricing">
                                        <div className="selected-plan-price-amount">{fmt(basePrice)}</div>
                                        <div className="selected-plan-billing-period">{planLabel}</div>
                                    </div>
                                </div>
                                <div className="selected-plan-inclusions">
                                    <span className="selected-plan-inclusions-label">Plan Includes:</span>
                                    <div className="plan-quota-stats-row">
                                        <div className="plan-quota-stat-box">
                                            <div className="plan-quota-stat-value">{(totalUsers ?? 0).toLocaleString('en-IN')}</div>
                                            <div className="plan-quota-stat-label">Users</div>
                                        </div>
                                        <div className="plan-quota-stat-box">
                                            <div className="plan-quota-stat-value">{(totalMaps ?? 0).toLocaleString('en-IN')}</div>
                                            <div className="plan-quota-stat-label">Map Calls</div>
                                        </div>
                                        <div className="plan-quota-stat-box">
                                            <div className="plan-quota-stat-value">{(totalAI ?? 0).toLocaleString('en-IN')}</div>
                                            <div className="plan-quota-stat-label">AI Credits</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Add-ons Card */}
                            <div className="plan-addons-card">
                                <div className="plan-addons-list">

                                    {/* Users */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">Users</div>
                                                <div className="plan-addon-description">Add team members to manage sales and operations</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('users', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.users}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('users', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.users > 0 ? `${currencySymbol}${p.users} / User / ${billing === 'yearly' ? 'Year' : 'Month'}` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Maps */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">
                                                    Maps Usage <span className="plan-addon-title-note">(in 1000 API calls)</span>
                                                </div>
                                                <div className="plan-addon-description">Used for tracking and route optimization</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('maps', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.maps}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('maps', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.maps > 0 ? `${currencySymbol}${p.maps} per 1000 calls` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* AI Credits */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">AI Credits</div>
                                                <div className="plan-addon-description">Used for AI summaries, suggestions, and automation</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('ai', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.ai}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('ai', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.ai > 0 ? `${currencySymbol}${p.ai} per 1000 credits` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Right Column — Order Summary ── */}
                        <div className="order-summary-wrapper">
                            <div className="order-summary-card">
                                <div className="order-summary-card-frame">
                                    <div className="order-summary-heading">Summary</div>
                                    <div className="order-summary-line-items">
                                        <div className="order-summary-line-group">
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">{capitalizeFirst(planName)}</span>
                                                <span className="order-summary-item-amount">{fmt(basePrice)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">Users({(totalUsers ?? 0).toLocaleString('en-IN')})</span>
                                                <span className="order-summary-item-amount">{fmt(userCost)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">Maps({(totalMaps ?? 0).toLocaleString('en-IN')})</span>
                                                <span className="order-summary-item-amount">{fmt(mapsCost)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">AI Credits({(totalAI ?? 0).toLocaleString('en-IN')})</span>
                                                <span className="order-summary-item-amount">{fmt(aiCost)}</span>
                                            </div>
                                        </div>

                                        {/* Credits & Adjustments — only in upgrade mode */}
                                        {isUpgradeMode && upgradeData && (
                                            <div className="order-summary-adjustments">
                                                <div className="order-summary-heading next-line">Credits & Adjustments</div>
                                                <div className="order-summary-line-group">
                                                    {upgradeData.proration?.base_plan_credit > 0 && (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">
                                                                Unused Plan Value
                                                                {upgradeData.proration.remaining_days && ` (${upgradeData.proration.remaining_days} days)`}
                                                            </span>
                                                            <span className="order-summary-item-amount">−{fmt(upgradeData.proration.base_plan_credit)}</span>
                                                        </div>
                                                    )}
                                                    {upgradeData.proration?.addon_user_credit > 0 && (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">Unused User Add-on</span>
                                                            <span className="order-summary-item-amount">−{fmt(upgradeData.proration.addon_user_credit)}</span>
                                                        </div>
                                                    )}

                                                    {upgradeData.proration?.addon_maps_credit > 0 ? (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">Unused Map Calls</span>
                                                            <span className="order-summary-item-amount">−{fmt(upgradeData.proration.addon_maps_credit)}</span>
                                                        </div>
                                                    ) : (upgradeData.proration?.unused_maps_count > 0 || upgradeData.new_plan?.carry_over_maps > 0) && (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">Carried-over Maps</span>
                                                            <span className="order-summary-item-amount">
                                                                {(upgradeData.new_plan?.carry_over_maps || upgradeData.proration?.unused_maps_count || 0).toLocaleString('en-IN')} Calls
                                                            </span>
                                                        </div>
                                                    )}

                                                    {upgradeData.proration?.addon_ai_credit > 0 ? (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">Unused AI Credits</span>
                                                            <span className="order-summary-item-amount">−{fmt(upgradeData.proration.addon_ai_credit)}</span>
                                                        </div>
                                                    ) : (upgradeData.proration?.unused_ai_count > 0 || upgradeData.new_plan?.carry_over_ai > 0) && (
                                                        <div className="order-summary-line-item">
                                                            <span className="order-summary-item-label">Carried-over AI Credits</span>
                                                            <span className="order-summary-item-amount">
                                                                {(upgradeData.new_plan?.carry_over_ai || upgradeData.proration?.unused_ai_count || 0).toLocaleString('en-IN')} Credits
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Total — use prorated price in upgrade mode */}
                                        <div className="order-summary-total-row">
                                            <span className="order-summary-total-label">Total Amount</span>
                                            <span className="order-summary-total-amount">
                                                {fmt(isUpgradeMode && upgradeData
                                                    ? Math.max(0, total - (upgradeData.new_plan?.proration_credit || upgradeData.proration?.total_credit || upgradeData.proration?.credit_amount || 0))
                                                    : total
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="order-summary-coupon-banner">
                                        <span>
                                            {billing === 'yearly'
                                                ? "Congrats! You've saved 30%"
                                                : 'Switch to yearly to save 30%'}
                                        </span>
                                    </div>

                                    {error && (
                                        <p className="plan-config-error-message">
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        className="sub-checkout-proceed-btn"
                                        onClick={handleContinue}
                                        disabled={isSubmitting}
                                        style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                    >
                                        {isSubmitting ? 'Processing…' : 'Continue to Payment'}
                                        {!isSubmitting && (
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="sub-checkout-proceed-btn-icon">
                                                <path d="M3.33337 8H12.6667" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M8 3.33301L12.6667 7.99967L8 12.6663" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </button>

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanConfigure;